import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
    ArrowsLeftRight,
    CheckCircle,
    CircleNotch,
    GitDiff,
    LinkBreak,
    MinusCircle,
    WarningCircle,
    X,
} from '@phosphor-icons/react';
import type { PreContractProduct } from '../lib/preContractTypes';
import type {
    ProductGenerateStep,
    ProductGenerateStepId,
} from '../lib/preContractProductGeneratePipeline';
import { canRunGenerateStep } from '../lib/preContractProductGeneratePipeline';
import type { SolarOneDrawDesignChange } from '../lib/preContractSolarDesignMetrics';
import type { PreContractUtilityDetailChange } from '../lib/preContractUtilityPrefill';

function displayProductName(product: PreContractProduct): string {
    if (product === 'RRR') return 'R&R';
    if (product === 'RR') return 'Remove & reinstall';
    return product;
}

function stepIcon(status: ProductGenerateStep['status']) {
    switch (status) {
        case 'running':
            return <CircleNotch size={16} weight="bold" className="pci-product-generate__icon--spin" aria-hidden />;
        case 'done':
        case 'synced':
            return <CheckCircle size={16} weight="fill" className="pci-product-generate__icon--ok" aria-hidden />;
        case 'skipped':
            return <MinusCircle size={16} weight="fill" className="pci-product-generate__icon--skip" aria-hidden />;
        case 'error':
            return <WarningCircle size={16} weight="fill" className="pci-product-generate__icon--err" aria-hidden />;
        default:
            return <span className="pci-product-generate__icon-dot" aria-hidden />;
    }
}

function stepStatusLabel(status: ProductGenerateStep['status']): string {
    switch (status) {
        case 'running':
            return 'Processing…';
        case 'done':
            return 'Complete';
        case 'synced':
            return 'Synced';
        case 'skipped':
            return 'Skipped';
        case 'error':
            return 'Failed';
        default:
            return 'Pending';
    }
}

export type PreContractProductGenerateProgressProps = {
    product: PreContractProduct | null;
    steps: ProductGenerateStep[];
    running?: boolean;
    /** Idle preview of the partner-specific pipeline (not currently generating). */
    preview?: boolean;
    partnerName?: string | null;
    onStepClick?: (stepId: ProductGenerateStepId) => void;
    stepDisabled?: boolean;
    stepDisabledReason?: string;
    designChanges?: SolarOneDrawDesignChange[];
    utilityChanges?: PreContractUtilityDetailChange[];
    onOpenLinkedQuote?: (quoteId: string) => void;
    onUnlinkQuote?: () => void;
};

export const PreContractProductGenerateProgress: React.FC<PreContractProductGenerateProgressProps> = ({
    product,
    steps,
    running = false,
    preview = false,
    partnerName = null,
    onStepClick,
    stepDisabled = false,
    stepDisabledReason,
    designChanges = [],
    utilityChanges = [],
    onOpenLinkedQuote,
    onUnlinkQuote,
}) => {
    const visibleSteps = steps.filter((step) => step.status !== 'skipped');
    const [changesKind, setChangesKind] = useState<'design' | 'utility' | null>(null);
    if (!product || visibleSteps.length === 0) return null;

    const popupChanges =
        changesKind === 'utility'
            ? utilityChanges
            : changesKind === 'design'
              ? designChanges
              : [];
    const changesPopup =
        changesKind && popupChanges.length > 0
            ? createPortal(
                  <div className="pci-design-changes" role="dialog" aria-modal="true" aria-label={`${changesKind === 'utility' ? 'Utility' : 'oneDRAW design'} changes`}>
                      <button
                          type="button"
                          className="pci-design-changes__backdrop"
                          aria-label="Close changes"
                          onClick={() => setChangesKind(null)}
                      />
                      <div className="pci-design-changes__panel">
                          <header className="pci-design-changes__head">
                              <div>
                                  <p className="pci-design-changes__kicker">
                                      {changesKind === 'utility' ? 'Utility' : 'oneDRAW'}
                                  </p>
                                  <h3 className="pci-design-changes__title">
                                      {changesKind === 'utility' ? 'Utility changes' : 'Design changes'}
                                  </h3>
                              </div>
                              <button
                                  type="button"
                                  className="pci-design-changes__close"
                                  aria-label="Close"
                                  onClick={() => setChangesKind(null)}
                              >
                                  <X size={16} weight="bold" />
                              </button>
                          </header>
                          <ul className="pci-design-changes__list" role="list">
                              {popupChanges.map((change) => (
                                  <li key={change.id} className="pci-design-changes__row">
                                      <span className="pci-design-changes__label">{change.label}</span>
                                      <div className="pci-design-changes__values">
                                          <span className="pci-design-changes__from">{change.from}</span>
                                          <ArrowsLeftRight size={14} weight="bold" aria-hidden />
                                          <span className="pci-design-changes__to">{change.to}</span>
                                      </div>
                                  </li>
                              ))}
                          </ul>
                      </div>
                  </div>,
                  document.body,
              )
            : null;

    return (
        <div
            className={`pci-product-generate${preview ? ' pci-product-generate--preview' : ''}`}
            role="status"
            aria-live={preview ? 'off' : 'polite'}
        >
            <div className="pci-product-generate__head">
                <span className="pci-product-generate__kicker">
                    {preview ? 'Required steps' : 'Running'}
                </span>
                <strong>{partnerName?.trim() || displayProductName(product)}</strong>
                {running ? <span className="pci-product-generate__running">In progress</span> : null}
            </div>
            <ol className="pci-product-generate__steps">
                {visibleSteps.map((step, index) => {
                    const previousReady = canRunGenerateStep(visibleSteps, step.id);
                    const waitingOnPrevious = !previousReady;
                    const lockedReason = waitingOnPrevious
                        ? `Complete ${visibleSteps[index - 1]?.label ?? 'the previous step'} first`
                        : stepDisabledReason;
                    const showDesignChanges =
                        step.id === 'finalize' && designChanges.length > 0;
                    const showUtilityChanges =
                        step.id === 'utilityDetails' && utilityChanges.length > 0;
                    const showChanges = showDesignChanges || showUtilityChanges;
                    const linkedQuoteId = step.id === 'quote' ? String(step.quoteId ?? '').trim() : '';
                    const hasLinkedQuote = Boolean(linkedQuoteId);
                    const canOpenQuote = hasLinkedQuote && Boolean(onOpenLinkedQuote);
                    const designBlocked = Boolean(step.blockedReason?.trim());
                    const canRun =
                        Boolean(onStepClick) &&
                        !stepDisabled &&
                        !designBlocked &&
                        step.status !== 'running' &&
                        step.status !== 'skipped' &&
                        previousReady &&
                        !hasLinkedQuote;
                    const isAction = canRun || canOpenQuote;
                    return (
                        <li
                            key={step.id}
                            className={`pci-product-generate__step pci-product-generate__step--${step.status}${
                                isAction ? ' pci-product-generate__step--action' : ''
                            }${showChanges ? ' pci-product-generate__step--changed' : ''}`}
                        >
                            <div className="pci-product-generate__step-row">
                                {canRun || canOpenQuote ? (
                                    <button
                                        type="button"
                                        className="pci-product-generate__step-btn"
                                        title={
                                            canOpenQuote
                                                ? `Open quote #${linkedQuoteId}`
                                                : (lockedReason ?? `Run ${step.label}`)
                                        }
                                        onClick={() =>
                                            canOpenQuote
                                                ? onOpenLinkedQuote?.(linkedQuoteId)
                                                : onStepClick?.(step.id)
                                        }
                                    >
                                        <span className="pci-product-generate__step-icon">
                                            {stepIcon(step.status)}
                                        </span>
                                        <div className="pci-product-generate__step-copy">
                                            <span className="pci-product-generate__step-label">
                                                {step.label}
                                            </span>
                                            <span className="pci-product-generate__step-status">
                                                {canOpenQuote
                                                    ? `Quote #${linkedQuoteId}`
                                                    : (step.detail ?? 'Click to run')}
                                            </span>
                                        </div>
                                    </button>
                                ) : (
                                    <div className="pci-product-generate__step-main">
                                        <span className="pci-product-generate__step-icon">
                                            {stepIcon(step.status)}
                                        </span>
                                        <div className="pci-product-generate__step-copy">
                                            <span className="pci-product-generate__step-label">
                                                {step.label}
                                            </span>
                                            <span className="pci-product-generate__step-status">
                                                {waitingOnPrevious
                                                    ? lockedReason
                                                    : (step.blockedReason ??
                                                          step.detail ??
                                                          stepStatusLabel(step.status))}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                {hasLinkedQuote && onUnlinkQuote ? (
                                    <button
                                        type="button"
                                        className="pci-product-generate__unlink-btn"
                                        title="Unlink and create a new quote"
                                        aria-label="Unlink and create a new quote"
                                        disabled={stepDisabled}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onUnlinkQuote();
                                        }}
                                    >
                                        <LinkBreak size={16} weight="bold" aria-hidden />
                                        <span>New quote</span>
                                    </button>
                                ) : null}
                                {showChanges ? (
                                    <button
                                        type="button"
                                        className={`pci-product-generate__changes-btn${
                                            (showDesignChanges && changesKind === 'design') ||
                                            (showUtilityChanges && changesKind === 'utility')
                                                ? ' pci-product-generate__changes-btn--on'
                                                : ''
                                        }`}
                                        title={
                                            showUtilityChanges
                                                ? 'View utility changes'
                                                : 'View design changes'
                                        }
                                        aria-expanded={
                                            showUtilityChanges
                                                ? changesKind === 'utility'
                                                : changesKind === 'design'
                                        }
                                        aria-haspopup="dialog"
                                        aria-label={
                                            showUtilityChanges
                                                ? 'View utility changes'
                                                : 'View oneDRAW design changes'
                                        }
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setChangesKind(showUtilityChanges ? 'utility' : 'design');
                                        }}
                                    >
                                        <GitDiff size={16} weight="bold" aria-hidden />
                                    </button>
                                ) : null}
                            </div>
                        </li>
                    );
                })}
            </ol>
            {changesPopup}
        </div>
    );
};

export default PreContractProductGenerateProgress;
